/**
 * The app's line icons, drawn to one set of rules so a row of them reads as one
 * family: a 24-unit box, a 1.5-unit stroke, round caps and joins, no fills, and
 * `currentColor` throughout so an icon is always exactly the colour of the text
 * it sits beside.
 *
 * Named rather than passed around as markup, because the point of a set is that
 * "the same thing looks the same everywhere" — `icon="trash"` on a delete
 * button in Settings and one in a project row can't drift apart, and a glyph
 * only has to be argued about once, here.
 *
 * The phone tab bar's four section icons are the same rules in ERB (see
 * shared/_app_tabbar.html.erb) rather than imports of this: the bar is
 * server-rendered on purpose, and a React root per link would be a lot of
 * machinery for four static glyphs. Keep the two in step by eye — they share
 * the box, the stroke and the caps, which is all that makes them a family.
 *
 * **The stroke came down from 2 to 1.5** (Alex, 2026-09-01, in two passes —
 * 1.7 first, then thinner again) — it read as chunky beside the redesign's
 * type, and 2 was inherited from the icon set this was drawn against rather
 * than chosen. The weight is stated per 24 units, so a glyph drawn in a
 * 20-unit box carries 1.25 to match and a 16-unit box carries 1.
 * Marks drawn smaller than 16px keep their own heavier stroke: at that size
 * a 1.5 hairline washes out, which is ordinary optical correction and not
 * drift.
 */

export type IconName =
  | 'plus'
  | 'arrowLeft'
  | 'trash'
  | 'pencil'
  | 'folder'
  | 'archive'
  | 'unarchive'
  | 'download'
  | 'upload'
  | 'image'
  | 'grip'
  | 'mail'
  | 'document'
  | 'check'
  | 'close'
  | 'chevronRight'
  | 'star'
  | 'starFilled'
  | 'merge'

// Paths only. Everything else about the drawing is fixed by the <svg> below,
// which is the whole reason these stay consistent.
const paths: Record<IconName, React.ReactNode> = {
  plus: <path d="M12 5v14M5 12h14" />,
  arrowLeft: <path d="M19 12H5M11 18l-6-6 6-6" />,
  // The lid drawn as its own stroke rather than as part of the body: at 16px
  // the classic one-piece bin loses the lid line entirely and reads as a cup.
  trash: (
    <>
      <path d="M4 7h16M10 4h4a1 1 0 0 1 1 1v2H9V5a1 1 0 0 1 1-1z" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  pencil: (
    <>
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="M14.5 6.5l3 3" />
    </>
  ),
  // Archive and unarchive are one box with the arrow turned around, the same
  // way download and upload are: putting a project away and taking it back out
  // are the same action in two directions, and should read as a pair.
  // Added with the Dropbox integration, which is entirely about folders — the
  // picker, the project link, and the template list all want one.
  folder: (
    <path d="M3 7a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
  ),
  archive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M12 11v6M9 14l3 3 3-3" />
    </>
  ),
  unarchive: (
    <>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M12 17v-6M9 14l3-3 3 3" />
    </>
  ),
  // Download and upload are the same tray with the arrow reversed, deliberately
  // — they are opposite halves of one idea and should look it.
  download: (
    <>
      <path d="M12 3v12M7 11l5 5 5-5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </>
  ),
  upload: (
    <>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="M21 15l-4.5-4.5L7 19" />
    </>
  ),
  // The flap drawn as its own stroke rather than crossing the corners, so at
  // 16px it still reads as an envelope instead of a rectangle with an X in it.
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 7.5l7.4 5.3a2 2 0 0 0 2.2 0l7.4-5.3" />
    </>
  ),
  // A page with a folded corner — the fold drawn as its own stroke so the
  // corner is legible at 16px, the same reasoning as mail's flap above.
  document: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
      <path d="M14 3v5h5" />
    </>
  ),
  // Only ever a statement that something is done — never a control. See
  // ContractShowPage, where it stands in the column a checkbox would occupy
  // for a phase that can still be billed.
  check: <path d="M5 13l4 4L19 7" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  // Points right when a group is shut and is rotated 90° when it opens, rather
  // than being a second glyph — one drawing that turns is what makes the two
  // states read as the same control.
  chevronRight: <path d="M9 6l6 6-6 6" />,
  // A drag handle, not a decoration — filled dots rather than the set's usual
  // stroked shapes, since at handle size a stroked circle disappears.
  grip: (
    <>
      <circle cx="9" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // The show thumbnail's marker, on a project's photos. Outline and filled are
  // the same five points, the same pairing as archive/unarchive — one glyph
  // that changes weight to say "not starred" versus "this one is".
  star: <path d="M12 3.5l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.4-4.2 6-.8z" />,
  starFilled: (
    <path
      d="M12 3.5l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.4-4.2 6-.8z"
      fill="currentColor"
    />
  ),
  // Two records joining into one — the familiar branch-merge shape, repurposed
  // here for combining a client, person or project pair rather than
  // anything Git-related.
  merge: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M6 8.5V17M18 15.5A9 9 0 0 1 9 6.5" />
    </>
  ),
}

interface IconProps {
  name: IconName
  /** Tailwind sizing/colour. Sized by the caller because a glyph in a button
   *  and one in a heading want different sizes off the same drawing. */
  className?: string
}

export function Icon({ name, className = 'h-4 w-4' }: IconProps) {
  return (
    <svg
      className={`shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decoration in every case this set is used for: an icon here always sits
      // beside a label that already says the same thing, so announcing it again
      // would just be the word twice.
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  )
}
